import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ClipboardList, Plus, Scale, Sprout, TrendingUp } from 'lucide-react';
import useSeoMeta from '../hooks/useSeoMeta';
import seoPaginas from '../content/seoPaginas.json';
import { useAuth } from '../contexts/AuthContext';
import PublicNav from '../components/common/PublicNav';
import PublicFooter from '../components/common/PublicFooter';
import { Eyebrow, Surcos } from '../components/landing/brand';
import { usePricesSnapshot } from '../hooks/usePricesSnapshot';
import PreciosTable from '../components/public/PreciosTable';
import PrecioTrendChart from '../components/public/PrecioTrendChart';
import CurrencyToggle, { type Currency } from '../components/public/CurrencyToggle';
import PreciosMagHoy from '../components/public/PreciosMagHoy';
import RematesRangosTable from '../components/public/RematesRangosTable';
import { usePreciosMag } from '../hooks/usePreciosMag';
import { fechaLargaMag, precioVigenteMag } from '../helpers/preciosMagHelper';
import ArrendamientoKilos, { type ReferenciaArrendamiento } from '../components/public/ArrendamientoKilos';
import { calcularArrendamiento, promedioPorKg } from '../helpers/arrendamientoHelper';
import { formatMonedaSimbolo } from '../libs/moneda';
import { formatNumber } from '../utils/formatNumber';
import faqPrecios from '../content/faq/precios.json';
import { ctaPublico } from '../helpers/ctaPublico';

// /precios — página que trae gente desde Google ("precio de la hacienda hoy").
// Manual de marca v1.0 (estilos en components/landing/brand.css).
//
// DOS FUENTES, siempre separadas y citadas:
//   - Precio del DÍA, oficial, del Mercado Agroganadero de Cañuelas (hacienda de
//     faena: novillos, novillitos, vaquillonas, vacas, toros, MEJ). Tabla
//     precios_mag_diarios, ver usePreciosMag.
//   - Promedio MENSUAL de remates de la provincia (no se nombra la fuente): terneros, terneras
//     e invernada/cría. Snapshot prices-snapshot.json (cattle_prices).
// Las preguntas frecuentes salen de src/content/faq/precios.json (el prerender
// arma el FAQPage desde el mismo archivo).

const FAQ = faqPrecios.preguntas;

const FUENTE_LINIERS =
  'https://news.agrofy.com.ar/noticia/199456/traslado-definitivo-mercado-liniers-tiene-fecha-cuando-operara-solo-canuelas';

// Lo que hace la cuenta con estos precios (verificado en la app: valuación del
// stock, lector + balanza por Bluetooth en el celular o USB en la compu, engorde).
const CON_TU_CUENTA = [
  { Icon: ClipboardList, title: 'Tu stock real', desc: 'Toma tus animales por categoría y peso, sin tipear nada.' },
  { Icon: Scale, title: 'Pesos de la balanza', desc: 'Conectás la balanza y el lector de caravana al celular o a la compu, y los pesos entran solos.' },
  { Icon: TrendingUp, title: 'Precios de referencia', desc: 'Valúa tu rodeo con los precios de referencia de cada mes, en pesos o en dólares.' },
  { Icon: Sprout, title: 'Engorde y margen', desc: 'Costo por kilo y rentabilidad del engorde, con tus costos reales.' },
];

const PreciosPage: React.FC = () => {
  const { user } = useAuth();

  useSeoMeta(seoPaginas.precios);

  const { data, loading, error } = usePricesSnapshot();
  const { dias: diasMag } = usePreciosMag();
  // Último día con datos; categorías sin precio ese día, con su último dato.
  const hoyMag = precioVigenteMag(diasMag);
  const [currency, setCurrency] = useState<Currency>('ARS');

  const usdRate = data?.usd.available ? data.usd.rate : null;
  const uvaToday = data?.uva.available ? data.uva.today : null;
  const uvaLatestMonth = data?.history?.length ? data.history[data.history.length - 1].uva : null;
  const actualizado = data?.latestMonth?.label;

  // Arrendamiento: kilos de ternero (promedio de remates del mes) o de novillo (último día de Cañuelas).
  const precioTernero = data?.available ? promedioPorKg(data.latest.terneros) : null;
  const novilloHoy = hoyMag?.categorias.find((c) => c.categoria === 'NOVILLOS')?.total ?? null;
  const referenciasArrendamiento: ReferenciaArrendamiento[] = [
    {
      clave: 'ternero',
      label: 'Ternero (promedio de remates)',
      detalle: `Ternero, promedio de remates de ${actualizado ?? 'el último mes'}`,
      precioKg: precioTernero,
    },
    {
      clave: 'novillo',
      label: 'Novillo (Mercado de Cañuelas)',
      detalle: novilloHoy ? `Novillo, Mercado de Cañuelas del ${fechaLargaMag(novilloHoy.fecha)}` : 'Novillo',
      precioKg: novilloHoy?.promedio ?? null,
    },
  ];
  const ejemploArrendamiento = calcularArrendamiento(50, 100, precioTernero);

  return (
    <div className="lp">
      <PublicNav />

      <main>
        {/* ───────────── Precio del día (oficial) ───────────── */}
        <section className="lp-ph">
          <div className="lp-wrap lp-ph__grid">
            <div>
              <Eyebrow>Mercado de Cañuelas · fuente oficial</Eyebrow>
              <h1>Precio de la hacienda hoy en Argentina</h1>
              <p className="lp-lead">
                Precio del día del novillo, novillito, vaquillona, vaca y toro en el Mercado Agroganadero de Cañuelas, y
                el valor del ternero en remates de invernada. Calculá cuánto vale tu rodeo y seguí la tendencia en pesos
                o dólares.
              </p>
              {actualizado && !hoyMag && (
                <span className="lp-stamp">
                  <i aria-hidden="true" />
                  Actualizado: {actualizado}
                </span>
              )}
              <div className="lp-ctas">
                <Link className="lp-btn lp-btn--primary" to="/calculadora-inventario">
                  Calcular el valor de mi rodeo <ArrowRight aria-hidden="true" />
                </Link>
                <Link className="lp-btn lp-btn--outline" to="/historico-precios">
                  Ver histórico
                </Link>
              </div>
            </div>

            {hoyMag ? (
              <PreciosMagHoy
                dia={hoyMag}
                currency={currency}
                usdRate={usdRate}
                toggle={
                  <CurrencyToggle
                    value={currency}
                    onChange={setCurrency}
                    disabled={!usdRate}
                    usdRate={usdRate}
                    uvaAvailable={!!uvaToday}
                  />
                }
              />
            ) : (
              <div className="lp-card lp-card--lift" id="cotizacion">
                <div className="lp-card__h">
                  <h2>Cotización del último mes por categoría</h2>
                  <CurrencyToggle
                    value={currency}
                    onChange={setCurrency}
                    disabled={!usdRate}
                    usdRate={usdRate}
                    uvaAvailable={!!uvaToday}
                  />
                </div>
                {loading ? (
                  <div className="lp-skel" style={{ height: 320 }} />
                ) : data?.available ? (
                  <PreciosTable
                    latest={data.latest}
                    currency={currency}
                    usdRate={usdRate}
                    uvaToday={uvaToday}
                    uvaLatestMonth={uvaLatestMonth}
                  />
                ) : (
                  <p className="lp-note">{error || 'Precios de referencia temporalmente no disponibles.'}</p>
                )}
                <p className="lp-note" style={{ marginTop: 14 }}>
                  Fuente: promedio mensual de remates de distintas zonas de la provincia de Buenos Aires. Precios por kilo vivo; en
                  otras zonas pueden diferir por flete y comisión.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* ───────────── Remates: promedio mensual (terneros e invernada) ───────────── */}
        <section className="lp-sec lp-sec--white" id="remates">
          <div className="lp-wrap lp-split" style={{ alignItems: 'start' }}>
            <div>
              <Eyebrow>Invernada y cría · remates</Eyebrow>
              <h2 className="lp-h3" style={{ marginTop: 12 }}>
                Precio del ternero hoy: promedio mensual en remates
              </h2>
              <p className="lp-body">
                El Mercado de Cañuelas comercializa hacienda de faena. Los terneros, las terneras y el resto de la
                invernada se venden en remates: acá está el promedio de {actualizado ?? 'el último mes cerrado'} por
                categoría y por rango de peso.
              </p>
              <p className="lp-note" style={{ marginTop: 16 }}>
                Fuente: promedio mensual de remates de distintas zonas de la provincia de Buenos Aires. Precios por kilo vivo.
              </p>
              {/* Sin precio del mercado, esta tabla ya está arriba (no se duplica). */}
              {hoyMag && (
                <div className="lp-card" style={{ marginTop: 24 }}>
                  <div className="lp-card__h">
                    <h3>Promedio del mes por categoría</h3>
                  </div>
                  {data?.available ? (
                    <PreciosTable
                      latest={data.latest}
                      currency={currency}
                      usdRate={usdRate}
                      uvaToday={uvaToday}
                      uvaLatestMonth={uvaLatestMonth}
                    />
                  ) : (
                    <p className="lp-note">{error || 'Precios de remates temporalmente no disponibles.'}</p>
                  )}
                </div>
              )}
            </div>
            <div>
              <h3 className="lp-h3" style={{ fontSize: 22 }}>
                Terneros y terneras por rango de peso
              </h3>
              <p className="lp-body" style={{ marginBottom: 16 }}>
                Precio por kilo según el peso del animal, por ejemplo terneros de 160 a 180 kg o de 180 a 200 kg.
              </p>
              {data?.available && (
                <RematesRangosTable
                  latest={data.latest}
                  currency={currency}
                  usdRate={usdRate}
                  uvaToday={uvaToday}
                  uvaLatestMonth={uvaLatestMonth}
                />
              )}
            </div>
          </div>
        </section>

        {/* ───────────── Tendencia ───────────── */}
        <section className="lp-sec lp-sec--white">
          <div className="lp-wrap">
            <div className="lp-sec__h">
              <h2 className="lp-h3">Tendencia de precios</h2>
              <Link className="lp-link" to="/historico-precios">
                Ver histórico completo <ArrowRight aria-hidden="true" />
              </Link>
            </div>
            {data ? (
              <PrecioTrendChart
                history={data.history.slice(-12)}
                usdRate={usdRate}
                uvaToday={uvaToday}
                subtitle="Últimos 12 meses · $/kg promedio mensual en remates de la provincia de Buenos Aires"
              />
            ) : (
              <div className="lp-skel" style={{ height: 380 }} />
            )}
          </div>
        </section>

        {/* ───────────── Arrendamiento en kilos ───────────── */}
        <section className="lp-sec" id="arrendamiento">
          <div className="lp-wrap lp-split" style={{ alignItems: 'start' }}>
            <div>
              <Eyebrow>Arrendamientos</Eyebrow>
              <h2 className="lp-h3" style={{ marginTop: 12 }}>
                Precio de la hacienda para arrendamiento
              </h2>
              <p className="lp-body">
                Muchos arrendamientos ganaderos se pactan en kilos de hacienda por hectárea al año y se pagan en pesos,
                al precio de referencia del momento del pago. Por eso el contrato tiene que decir qué precio se usa: la
                categoría (ternero o novillo), el mercado o los remates de referencia y la fecha.
              </p>
              {ejemploArrendamiento && precioTernero && (
                <p className="lp-body">
                  Por ejemplo, 50 kg de ternero por hectárea al año sobre 100 hectáreas son{' '}
                  {formatNumber(ejemploArrendamiento.kgTotales)} kg. Con el promedio de remates de{' '}
                  {actualizado ?? 'el último mes'} ({formatMonedaSimbolo(precioTernero, 'ARS')} por kilo), el alquiler
                  anual es de <strong>{formatMonedaSimbolo(ejemploArrendamiento.montoAnual, 'ARS')}</strong>.
                </p>
              )}
              <p className="lp-note">
                Es una cuenta de referencia: vale lo que diga tu contrato sobre la categoría, el mercado, la fecha del
                precio y si se descuentan gastos.
              </p>
            </div>
            <ArrendamientoKilos referencias={referenciasArrendamiento} />
          </div>
        </section>

        {/* ───────────── Cómo se forma el precio (E-E-A-T) + calculadora ───────────── */}
        <section className="lp-sec">
          <div className="lp-wrap lp-split" style={{ alignItems: 'start' }}>
            <div>
              <h2 className="lp-h3">¿Cómo se forma el precio de la hacienda?</h2>
              <p className="lp-body">
                El precio del ganado bovino lo determina la oferta y la demanda en los mercados concentradores. En
                Argentina, el <strong>Mercado Agroganadero de Cañuelas (MAG)</strong> es una de las principales
                referencias: allí concurren frigoríficos exportadores, supermercados e invernadores, y los precios
                quedan publicados por categoría y rango de peso.
              </p>
              <p className="lp-body">
                Los factores que más mueven el precio son: la faena y la disponibilidad de animales terminados, el
                ritmo de las exportaciones, el tipo de cambio (un dólar más alto suele empujar el precio en pesos) y la
                estacionalidad de la oferta (más abundante en otoño, más escasa en primavera).
              </p>
              <h3 className="lp-h3" style={{ fontSize: 22, marginTop: 28 }}>
                ¿Y el Mercado de Liniers?
              </h3>
              <p className="lp-body">
                El Mercado de Liniers operó por última vez el viernes 13 de mayo de 2022. Desde el 17 de mayo de 2022 la
                hacienda se comercializa en el Mercado Agroganadero de Cañuelas, que es su continuación: el precio de
                Liniers hoy es el precio de Cañuelas (
                <a href={FUENTE_LINIERS} target="_blank" rel="noopener noreferrer">
                  Agrofy News
                </a>
                ).
              </p>
              <p className="lp-note" style={{ marginTop: 20 }}>
                Fuentes de referencia:{' '}
                <a href="https://www.mercadoagroganadero.com.ar" target="_blank" rel="noopener noreferrer">MAG Cañuelas</a>
                {' · '}
                <a href="https://www.ipcva.com.ar" target="_blank" rel="noopener noreferrer">IPCVA</a>
                {' · '}
                <a href="https://www.argentina.gob.ar/inta" target="_blank" rel="noopener noreferrer">INTA</a>
              </p>
            </div>

            <div className="lp-callout">
              <h2>Calculá el valor de tu rodeo en 1 minuto</h2>
              <p>
                Cargá cuántas cabezas tenés de cada categoría y su peso promedio, y te decimos cuánto vale tu inventario
                hoy, en pesos o dólares, con estos mismos precios.
              </p>
              <Link className="lp-btn lp-btn--primary" to="/calculadora-inventario">
                Probar la calculadora de inventario <ArrowRight aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* ───────────── Con tu cuenta ───────────── */}
        <section className="lp-sec lp-dark">
          <div className="lp-wrap">
            <div className="lp-sec-h">
              <Eyebrow>Con tu cuenta gratis</Eyebrow>
              <h2 className="lp-h2">En MiCampoDigital, tu rodeo se valúa solo.</h2>
              <p className="lp-lead">
                En vez de cargar a mano, el valor de tu inventario sale de tus animales reales y de los precios de
                referencia de cada mes.
              </p>
            </div>
            <div className="lp-perks">
              {CON_TU_CUENTA.map(({ Icon, title, desc }) => (
                <div key={title} className="lp-perk">
                  <Icon className="lp-ico" aria-hidden="true" />
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
            <div className="lp-ctas">
              <Link className="lp-btn lp-btn--primary" to={ctaPublico(user).to}>
                {ctaPublico(user).label} <ArrowRight aria-hidden="true" />
              </Link>
            </div>
          </div>
          <Surcos color="blanco" style={{ right: -120, bottom: -260, width: 860 }} />
        </section>

        {/* ───────────── Preguntas frecuentes ───────────── */}
        <section className="lp-sec lp-faq">
          <div className="lp-wrap lp-split">
            <div className="lp-sec-h">
              <Eyebrow>Preguntas</Eyebrow>
              <h2 className="lp-h2">Preguntas frecuentes</h2>
            </div>
            <div>
              {FAQ.map((item, i) => (
                <details key={item.q} open={i === 0}>
                  <summary>
                    {item.q}
                    <Plus aria-hidden="true" />
                  </summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
};

export default PreciosPage;
